import { AdminPageHeader } from '@/components/patterns/admin-page'
import LinkCard from '@/components/patterns/link-card'
import { Badge } from '@/components/ui/badge'
import { CardContent } from '@/components/ui/card'
import { StyledIcon } from '@/components/ui/styled-icon'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import { BetaFeatureKey, hasBetaFeature } from '@/lib/beta-features'
import { canAccessUsersInOrganization, isGlobalOrganization } from '@/lib/user-access'
import { ConnectAccountStatus } from '@/lib/enums/ConnectAccountStatus'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import { notificationEventQueries } from '@/lib/services/notification-events/notification-events.query'
import { notificationRecipientQueries } from '@/lib/services/notification-recipients/notification-recipients.query'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { stripeConnectQueries } from '@/lib/services/stripe-connect/stripe-connect.query'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, notFound, useParams } from '@tanstack/react-router'
import {
  ArrowRight,
  Banknote,
  Bell,
  BadgeCent,
  Clock,
  Coins,
  CreditCard,
  Landmark,
  MapPin,
  Network,
  ReceiptText,
  Repeat2,
  Printer,
  Settings,
  ShoppingCart,
  Ticket,
  Truck,
  Users,
} from 'lucide-react'

const locationIndexFilters = locationFilters().get()

const businessSettingsSections = [
  { id: 'shop', title: 'Your shop', description: 'Hours, delivery, and where you trade.' },
  { id: 'money', title: 'Money', description: 'How you bill, collect payments, and handle taxes.' },
  { id: 'gear', title: 'Your gear', description: 'Printers, card readers, and what you print.' },
  { id: 'people', title: 'Your people', description: 'Who works here and what reaches them.' },
]

export function groupBusinessSettingsUseCases(useCases) {
  return businessSettingsSections
    .map((section) => ({
      ...section,
      items: useCases.filter((useCase) => useCase.section === section.id),
    }))
    .filter((section) => section.items.length > 0)
}

function canManageNotifications(ability) {
  return (
    ability?.can('edit', 'notification-recipients') || ability?.can('edit', 'notification-events')
  )
}

function canAccessSubject(ability, subject) {
  return (
    ability?.can('list', subject) || ability?.can('view', subject) || ability?.can('edit', subject)
  )
}

function canManageSubject(ability, subject) {
  return ability?.can('edit', subject)
}

export function hasBusinessSettingsAccess(ability) {
  return (
    canAccessSubject(ability, 'business-settings') ||
    canAccessSubject(ability, 'subscription-settings') ||
    ability?.can('list', 'fee-settings') ||
    ability?.can('view', 'cash-drawer-settings')
  )
}

export function getStripeSummaryMeta(status = null) {
  switch (status) {
    case ConnectAccountStatus.Verified.Value:
      return { label: 'Connected', variant: 'success' }
    case ConnectAccountStatus.PendingVerification.Value:
      return { label: 'Pending review', variant: 'warning' }
    case ConnectAccountStatus.Restricted.Value:
      return { label: 'Needs attention', variant: 'warning' }
    case ConnectAccountStatus.Incomplete.Value:
    case ConnectAccountStatus.NotCreated.Value:
      return { label: 'Needs setup', variant: 'outline' }
    default:
      return { label: 'Checking status', variant: 'muted' }
  }
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function getLocationActionLink({ orgId, path, locations, destination }) {
  if (locations.length !== 1) {
    return path.locations.index().using({ orgId })
  }

  return path[destination].index().using({ orgId, locationId: locations[0]?.id })
}

function getLocationActionMeta(locations) {
  if (locations.length === 0) {
    return {
      badgeLabel: 'Add a location first',
      badgeVariant: 'outline',
    }
  }

  if (locations.length > 1) {
    return {
      badgeLabel: 'Choose a location',
      badgeVariant: 'outline',
    }
  }

  return {}
}

export function buildBusinessSettingsUseCases({
  ability,
  organization,
  orgId,
  path,
  locations,
  notificationRecipientCount,
  stripeStatus,
}) {
  const canManageNotificationSettings = canManageNotifications(ability)
  const canEditStripe = ability.can('edit', 'stripe-account')
  const locationActionMeta = getLocationActionMeta(locations)
  const stripeMeta = getStripeSummaryMeta(stripeStatus?.status)

  const stripeUseCase =
    stripeStatus?.status === ConnectAccountStatus.Verified.Value
      ? {
          description: 'Check deposits, account details, and anything that needs attention.',
        }
      : {
          description: 'Connect your account so you can take payments and receive payouts.',
        }

  return [
    {
      id: 'locations',
      section: 'shop',
      title: 'Locations',
      description: 'Addresses and shop details.',
      icon: MapPin,
      link: path.locations.index().using({ orgId }),
      show: ability.can('list', 'locations'),
    },
    {
      id: 'hours',
      section: 'shop',
      title: 'Store hours & pickup',
      description: 'Set your opening hours and when customers can pick up orders.',
      icon: Clock,
      link: getLocationActionLink({
        orgId,
        path,
        locations,
        destination: 'hours',
      }),
      show: ability.can('list', 'locations') && canAccessSubject(ability, 'location-hours'),
      ...locationActionMeta,
    },
    {
      id: 'deliveries',
      section: 'shop',
      title: 'Delivery',
      description: 'Manage where you deliver, when you deliver, and what customers pay.',
      icon: Truck,
      link: getLocationActionLink({
        orgId,
        path,
        locations,
        destination: 'deliveries',
      }),
      show:
        ability.can('list', 'locations') &&
        (canAccessSubject(ability, 'location-delivery') ||
          canAccessSubject(ability, 'location-delivery-pricing')),
      ...locationActionMeta,
    },
    {
      id: 'billing',
      section: 'money',
      title: 'Customer billing',
      description: 'Choose Pay Later options, credit limits, and payment terms for new customers.',
      icon: ReceiptText,
      link: path.billing.index().using({ orgId }),
      show: canAccessSubject(ability, 'billing-settings'),
    },
    {
      id: 'fees',
      section: 'money',
      title: 'Shop fees',
      description: 'Add your own fee to orders. Your shop keeps it.',
      icon: Coins,
      link: path.fees.index().using({ orgId }),
      show: ability.can('list', 'fee-settings'),
    },
    {
      id: 'cash-drawer',
      section: 'money',
      title: 'Cash drawer tracking',
      description: 'Set up registers and reconcile each cash drawer’s cash and checks.',
      icon: Banknote,
      link: path.cashDrawerSettings.index().using({ orgId }),
      show: !isGlobalOrganization(organization) && ability.can('view', 'cash-drawer-settings'),
    },
    {
      id: 'taxes',
      section: 'money',
      title: 'Taxes',
      description: 'Sales tax and delivery tax settings.',
      icon: BadgeCent,
      link: path.taxes.index().using({ orgId }),
      show: canAccessSubject(ability, 'tax-settings'),
    },
    {
      id: 'checkout',
      section: 'money',
      title: 'Choose what customers see at checkout',
      description: 'Turn website tips on or off for online orders.',
      icon: ShoppingCart,
      link: path.checkout.index().using({ orgId }),
      show: canAccessSubject(ability, 'checkout-settings'),
    },
    {
      id: 'subscription-terms',
      section: 'money',
      title: 'Subscription terms',
      description: 'Choose the terms customers accept when starting a subscription.',
      icon: Repeat2,
      link: {
        href: `/${orgId}/settings/subscriptions`,
        link: {
          to: '/$orgId/settings/subscriptions',
          params: { orgId },
        },
      },
      show: canAccessSubject(ability, 'subscription-settings'),
    },
    {
      id: 'supertickets',
      section: 'gear',
      title: 'Supertickets',
      description: 'Adjust ticket layouts, branding space, and the details your shop prints.',
      icon: Ticket,
      link: path.supertickets.index().using({ orgId }),
      show: ability.can('edit', 'super-ticket-settings') || ability.can('view', 'sales-orders'),
    },
    {
      id: 'printers',
      section: 'gear',
      title: 'Printers',
      description: 'Choose local and automatic printing options and review print jobs.',
      icon: Printer,
      link: path.snapdragon.index().using({ orgId }),
      show:
        canAccessSubject(ability, 'snapdragon-settings') &&
        hasBetaFeature(organization, BetaFeatureKey.Snapdragon),
    },
    {
      id: 'integrations',
      section: 'gear',
      title: 'Integrations',
      description: 'Connect services like Flower Shop Network and Condolences.',
      icon: Network,
      link: path.integrations.index().using({ orgId }),
      show:
        canManageSubject(ability, 'fsn-network') ||
        canAccessSubject(ability, 'meta-pixel') ||
        canAccessSubject(ability, 'google-tag-manager') ||
        canAccessSubject(ability, 'snapdragon-settings') ||
        canAccessSubject(ability, 'condolences') ||
        canAccessSubject(ability, 'google-analytics'),
    },
    {
      id: 'notifications',
      section: 'people',
      title: 'Notifications',
      description: 'Choose who gets order, fulfillment, and shop notifications.',
      icon: Bell,
      link: path.notifications.index().using({ orgId }),
      show: canManageNotificationSettings,
      badgeLabel:
        notificationRecipientCount > 0
          ? pluralize(notificationRecipientCount, 'recipient')
          : 'Needs setup',
      badgeVariant: notificationRecipientCount > 0 ? 'muted' : 'outline',
    },
    {
      id: 'terminals',
      section: 'gear',
      title: 'Card readers',
      description: 'Set up or reset your POS card readers.',
      icon: CreditCard,
      link: path.terminals.index().using({ orgId }),
      show: canAccessSubject(ability, 'terminals'),
    },
    {
      id: 'stripe',
      section: 'money',
      title: 'Payment account',
      icon: Landmark,
      link: path.stripe.index().using({ orgId }),
      show: canEditStripe,
      badgeLabel: stripeMeta.label,
      badgeVariant: stripeMeta.variant,
      ...stripeUseCase,
    },
    {
      id: 'users',
      section: 'people',
      title: 'Staff access',
      description: 'Add teammates and adjust what each person can see or change.',
      icon: Users,
      link: path.users.index().using({ orgId }),
      show: canAccessUsersInOrganization(ability, organization),
    },
  ].filter((useCase) => useCase.show)
}

function BusinessSettingsUseCaseCard({ useCase }) {
  return (
    <LinkCard {...(useCase.link.link ?? useCase.link)} size="sm" className="h-full">
      <CardContent size="sm" className="flex items-start gap-3">
        <StyledIcon icon={useCase.icon} tone="brand" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Typography variant="cardTitle" as="h3" className="min-w-0 flex-1">
              {useCase.title}
            </Typography>
            {useCase.badgeLabel ? (
              <Badge variant={useCase.badgeVariant}>{useCase.badgeLabel}</Badge>
            ) : null}
          </div>
          <Typography variant="supporting" className="block">
            {useCase.description}
          </Typography>
        </div>
        <ArrowRight
          aria-hidden="true"
          className="size-4 shrink-0 self-center text-muted-foreground"
        />
      </CardContent>
    </LinkCard>
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/')({
  component: BusinessSettingsPage,
  loader: async ({ context, params }) => {
    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.orgId)
    )

    if (!hasBusinessSettingsAccess(context.ability, organization)) throw notFound()

    const queries = []
    const canManageNotificationSettings = canManageNotifications(context.ability)
    const canViewNotificationRecipients =
      canManageNotificationSettings && context.ability.can('list', 'notification-recipients')
    const canViewNotificationEvents =
      canManageNotificationSettings && context.ability.can('list', 'notification-events')

    if (context.ability.can('list', 'locations')) {
      queries.push(
        context.queryClient.ensureQueryData(
          locationQueries.index({
            organizationId: params.orgId,
            filters: locationIndexFilters,
          })
        )
      )
    }

    if (canViewNotificationRecipients) {
      queries.push(
        context.queryClient.ensureQueryData(
          notificationRecipientQueries.index({ organizationId: params.orgId })
        )
      )
    }

    if (canViewNotificationEvents) {
      queries.push(
        context.queryClient.ensureQueryData(
          notificationEventQueries.index({ organizationId: params.orgId })
        )
      )
    }

    if (context.ability.can('edit', 'stripe-account')) {
      queries.push(
        context.queryClient.ensureQueryData(
          stripeConnectQueries.status({ organizationId: params.orgId })
        )
      )
    }

    await Promise.all(queries)
  },
})

export function BusinessSettingsPage() {
  const ability = useAbility()
  const { orgId } = useParams({ strict: false })
  const path = usePathHelper()
  const { organization } = useOrganizationContext()

  const canViewLocations = ability.can('list', 'locations')
  const canManageNotificationSettings = canManageNotifications(ability)
  const canViewNotificationRecipients = ability.can('list', 'notification-recipients')
  const canEditStripe = ability.can('edit', 'stripe-account')

  const { data: locations = [] } = useQuery({
    ...locationQueries.index({
      organizationId: orgId,
      filters: locationIndexFilters,
    }),
    enabled: canViewLocations,
  })
  const { data: notificationRecipients = [] } = useQuery({
    ...notificationRecipientQueries.index({ organizationId: orgId }),
    enabled: canManageNotificationSettings && canViewNotificationRecipients,
  })
  const { data: stripeStatus = null } = useQuery({
    ...stripeConnectQueries.status({ organizationId: orgId }),
    enabled: canEditStripe,
  })
  const useCases = buildBusinessSettingsUseCases({
    ability,
    organization,
    orgId,
    path,
    locations,
    notificationRecipientCount: notificationRecipients.length,
    stripeStatus,
  })
  const sections = groupBusinessSettingsUseCases(useCases)

  return (
    <div className="@container/settings max-w-6xl space-y-8 pb-6">
      <AdminPageHeader
        icon={Settings}
        title="Settings"
        description="Manage your shop, payments, equipment, and team."
      />

      {sections.map((section) => (
        <section key={section.id} aria-labelledby={`settings-${section.id}`} className="space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Typography id={`settings-${section.id}`} variant="sectionTitle" as="h2">
              {section.title}
            </Typography>
            <Typography variant="description">{section.description}</Typography>
          </div>
          <div className="grid gap-3 @3xl/settings:grid-cols-2">
            {section.items.map((useCase) => (
              <BusinessSettingsUseCaseCard key={useCase.id} useCase={useCase} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
